import React, {
  ReactNode,
  useCallback,
  useRef,
  MouseEvent,
  useState,
  ComponentProps,
  useEffect,
} from 'react'

import { Keyframes, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useDebouncedCallback } from 'use-debounce'

import { useBodyScrollLock } from '@surglogs-app/hooks'
import { Cross } from '@surglogs-app/icons'
import { isClickOutsideRef } from '@surglogs-app/util'

import { useLayerManager, useOffsetManagerContext } from '../../layout'

import { Box } from './Box'
import { FlexBox } from './FlexBox'
import { Icon } from './Icon'

const SHEET_MAX_WIDTH = 1200
const ANIMATION_DURATION = 500
const TRANSITION = '500ms cubic-bezier(0.645, 0.045, 0.355, 1)'
// 32 + 16 gap
const CLOSE_ICON_WIDTH = 48
const PADDING = 16

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(100% + ${PADDING}px));
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const SheetWrapper = styled(Box)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: ${({ theme }) => theme.ui.spacings.md};
`

const SheetBackground = styled(SheetWrapper)`
  background-color: rgba(0, 0, 0, 0.5);
  transition: opacity ${TRANSITION};
  animation: ${fadeIn} ${TRANSITION};
`

const SheetContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'animation',
})<{ animation: Keyframes }>`
  margin-left: auto;
  animation: ${({ animation }) => animation} ${TRANSITION};
`

const CloseIcon = styled(Icon)`
  margin-right: auto;
`

const HeaderContent = styled(Box)`
  margin-right: auto;
`

interface SheetProps
  extends Omit<ComponentProps<typeof SheetContent>, 'animation'> {
  onClose: () => void
  children: ReactNode
  headerContent?: ReactNode
  maxWidth?: number
}

export const Sheet = ({
  onClose,
  children,
  headerContent,
  maxWidth = SHEET_MAX_WIDTH,
  ...rest
}: SheetProps) => {
  const [animation, setAnimation] = useState(slideInFromRight)
  const [backgroundOpacity, setBackgroundOpacity] = useState(1)
  const [isAnimating, setIsAnimating] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { top: offset } = useOffsetManagerContext()
  const { increaseBy, zIndex } = useLayerManager()

  // prevent user from clicking on the background while the sheet is animating
  useEffect(() => {
    setIsAnimating(true)

    const timer = setTimeout(() => {
      setIsAnimating(false)
    }, ANIMATION_DURATION)

    return () => clearTimeout(timer)
  }, [])

  useBodyScrollLock(contentRef)

  const debouncedClose = useDebouncedCallback(onClose, ANIMATION_DURATION)

  const onSheetClose = useCallback(() => {
    debouncedClose()
    setIsAnimating(true)
    setAnimation(slideOutToRight)
    setBackgroundOpacity(0)
  }, [debouncedClose])

  const onBackgroundClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isClickOutsideRef(contentRef, e) && !isAnimating) {
        onSheetClose()
      }
    },
    [isAnimating, onSheetClose],
  )

  return (
    <>
      <SheetBackground
        {...{
          top: offset,
          zi: zIndex + increaseBy + 1,
          opacity: backgroundOpacity,
        }}
      />
      <SheetWrapper
        {...{
          zi: zIndex + increaseBy + 2,
          ref: wrapperRef,
          onClick: onBackgroundClick,
        }}>
        <SheetContent
          {...{
            position: 'relative',
            overflow: 'auto',
            bgc: 'white.100',
            h: '100%',
            w: '50%',
            bra: 'md',
            ref: contentRef,
            animation,
            maw: maxWidth,
            ...rest,
            role: 'dialog',
          }}>
          <FlexBox
            {...{
              position: 'sticky',
              top: '0',
              bottom: '0',
              bgc: 'white.100',
              padding: 'abs.12',
              bb: '1px solid',
              bc: 'navy.10',
              jc: 'center',
              ai: 'center',
              gap: 'md',
            }}>
            <CloseIcon
              {...{
                icon: Cross,
                size: 'xl',
                cursor: 'pointer',
                onClick: onSheetClose,
                role: 'button',
                'aria-label': 'Close sheet',
              }}
            />
            <HeaderContent {...{ w: `calc(100% - ${CLOSE_ICON_WIDTH}px)` }}>
              {headerContent}
            </HeaderContent>
          </FlexBox>
          {children}
        </SheetContent>
      </SheetWrapper>
    </>
  )
}
